'use client';

/**
 * API namespace `push`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L9037-L9132).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const push = {
  /**
   * GET /api/notifications/push/vapid-public-key
   *
   * Public endpoint (no auth). The browser fetches this BEFORE calling
   * `pushManager.subscribe({ applicationServerKey })` so it can convert
   * the base64url VAPID key to a Uint8Array.
   *
   * @returns `{ publicKey: string }` — the VAPID public key (base64url).
   * @throws 503 `PUSH_DISABLED` if the feature flag is off.
   * @throws 503 `PUSH_NOT_CONFIGURED` if the flag is on but VAPID keys
   *         are not set in the env.
   */
  async getVapidPublicKey(signal?: AbortSignal): Promise<{ publicKey: string }> {
    // `auth: false` because the endpoint is PUBLIC (the browser may want
    // to subscribe on first paint before the user is logged in — see
    // `push.controller.ts:21-29`). `skipRefresh: true` prevents the 401
    // → refresh cascade for anonymous users.
    return apiFetch<{ publicKey: string }>(
      '/notifications/push/vapid-public-key',
      { auth: false, skipRefresh: true, signal },
    );
  },
  /**
   * POST /api/notifications/push/subscribe
   *
   * Auth: JWT required (handled by `requireAuth` middleware on the route).
   * Body: the W3C PushSubscription shape (`endpoint` + `keys.p256dh` +
   * `keys.auth` + optional `userAgent`) — validated by the shared Zod
   * schema `pushSubscribeSchema` on BOTH ends of the wire.
   *
   * @returns `{ subscriptionId: string; endpoint: string }` — the
   *          server-generated subscription ID + the echoed endpoint
   *          (the browser does NOT need to send the ID back on
   *          unsubscribe — the endpoint is the canonical id per
   *          W3C `PushSubscription.unsubscribe()`).
   */
  async subscribe(
    body: PushSubscribeInput,
    signal?: AbortSignal,
  ): Promise<{ subscriptionId: string; endpoint: string }> {
    return apiFetch<{ subscriptionId: string; endpoint: string }>(
      '/notifications/push/subscribe',
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
  /**
   * DELETE /api/notifications/push/subscribe
   *
   * Auth: JWT required. Body: `{ endpoint }` — idempotent (calling
   * unsubscribe on an already-removed subscription is a no-op, returns
   * 200 with `{ ok: true }`).
   *
   * @returns `{ ok: true }` on success.
   */
  async unsubscribe(
    body: PushUnsubscribeInput,
    signal?: AbortSignal,
  ): Promise<{ ok: boolean }> {
    return apiFetch<{ ok: boolean }>('/notifications/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify(body),
      signal,
    });
  },
};
